import { resolveMpmHovTargetSlotCount } from "@/features/hoverIdle/mpm/mpmHovTargetSlots";
import {
  createHovGainUpgradeMpTargetFromSlotIndex,
  toHovGainUpgradeMpTargetKey,
  type HovGainUpgradeMpTarget,
  type HovGainUpgradeMpTargetKey,
} from "@/features/hoverIdle/mpm/mpmHovGainUpgradeTargets";
import { computeMpmAvailableMpFromOccupancy } from "@/features/hoverIdle/mpm/mpmCapacity";
import type { MpmState } from "@/features/hoverIdle/mpm/mpmState";

export type { HovGainUpgradeMpTarget };

export interface MpmHovGainUpgradeSlot {
  readonly index: number;
  readonly label: string;
  readonly target: HovGainUpgradeMpTarget;
  readonly assigned: boolean;
  readonly disabled: boolean;
}

export interface MpmHovGainUpgradeSlotInput {
  readonly mpCapacity: number;
  readonly mpm: MpmState;
}

export function createMpmHovGainUpgradeSlots(
  input: MpmHovGainUpgradeSlotInput,
): readonly MpmHovGainUpgradeSlot[] {
  if (!input.mpm.hovGainUpgrades.unlocked) {
    return [];
  }

  const hovTargetSlotCount = resolveMpmHovTargetSlotCount(
    input.mpm.gridUnlock.hov,
  );
  const assignedTargetKeys = new Set(
    input.mpm.hovGainUpgrades.assignments
      .map((target) => toHovGainUpgradeMpTargetKey(target))
      .filter((key): key is HovGainUpgradeMpTargetKey => key !== null),
  );
  const availableMp = computeMpmAvailableMpFromOccupancy({
    mpCapacity: input.mpCapacity,
    state: input.mpm,
  });
  const canAssign = input.mpm.hovGainUpgrades.enabled && availableMp > 0;

  return Array.from({ length: hovTargetSlotCount + 1 }, (_, index) => {
    const target = createHovGainUpgradeMpTargetFromSlotIndex(index);
    if (target === null) {
      throw new Error(`invalid Hov Gain Upgrade slot index: ${index}`);
    }
    const targetKey = toHovGainUpgradeMpTargetKey(target);
    const assigned = targetKey !== null && assignedTargetKeys.has(targetKey);
    return {
      index,
      label: index === 0 ? "HG" : `H${index}G`,
      target,
      assigned,
      disabled: !assigned && !canAssign,
    };
  });
}
